import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import {
  Avatar,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Divider,
  IconButton,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SortRoundedIcon from "@mui/icons-material/SortRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import { useAuth } from "contexts/AuthContext";
import { useStateContext } from "contexts/ContextProvider";
import { useWebSocket } from "contexts/WebSocketContext";
import { usePageTitle } from "hooks/usePageTitle";
import { OVERLAY_NAMES } from "constants/Overlays";
import api from "services/api";
import { NAVBAR_HEIGHT } from "constants/layout";
import { getAvatarColor } from "helpers/avatarHelpers";
import GroupedSidebarNav from "components/GroupedSidebarNav";
import MemberActivity from "components/MemberActivity";
import CustomDateSelector from "components/CustomDateSelector";
import ExpensesSettings from "./ExpensesSettings";
import NotificationSettingsTab from "./NotificationSettingsTab";
import PortalEmailTemplatesTab from "./PortalEmailTemplatesTab";
import TimesheetsTab from "./TimesheetsTab";

import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useLocation, useSearchParams } from "react-router-dom";

const SettingsCard = ({
  children,
  theme,
  sx,
  disableIconButtonOverrides = false,
}) => (
  <Box
    sx={{
      bgcolor: theme.palette.background.default,
      borderRadius: "12px",
      border: `1px solid ${theme.palette.primary.border2}`,
      p: { xs: "0.75rem", sm: "1.25rem" },
      mx: "0.5rem",
      mt: "0.25rem",
      boxShadow: theme.shadows[3],
      maxWidth: "100%",
      boxSizing: "border-box",
      ...(disableIconButtonOverrides
        ? {}
        : {
            "& .MuiIconButton-root": {
              width: 36,
              height: 36,
              padding: 0,
              borderRadius: "12px",
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.primary.border2}`,
              color: theme.palette.primary.main,
            },
            "& .MuiIconButton-root .MuiSvgIcon-root": {
              fontSize: 20,
            },
            "& .MuiIconButton-root.MuiIconButton-sizeSmall": {
              width: 36,
              height: 36,
              padding: 0,
            },
            "& .MuiIconButton-root.MuiIconButton-sizeLarge": {
              width: 36,
              height: 36,
              padding: 0,
            },
            "& .MuiCard-root .MuiIconButton-root": {
              width: 30,
              height: 30,
            },
            "& .MuiCard-root .MuiIconButton-root .MuiSvgIcon-root": {
              fontSize: 18,
            },
            "& .MuiIconButton-root:hover": {
              backgroundColor: theme.palette.action.hover,
            },
          }),
      ...sx,
    }}
  >
    {children}
  </Box>
);

const SettingsGridCard = ({
  children,
  theme,
  sx,
  disableIconButtonOverrides = false,
}) => (
  <Box
    sx={{
      bgcolor: theme.palette.background.default,
      borderRadius: "12px",
      border: `1px solid ${theme.palette.primary.border2}`,
      //p: { xs: "0.25rem", sm: "0.25rem" },
      mx: "0.5rem",
      mt: "0.25rem",
      boxShadow: theme.shadows[3],
      maxWidth: "100%",
      boxSizing: "border-box",
      ...(disableIconButtonOverrides
        ? {}
        : {
            "& .MuiIconButton-root": {
              width: 36,
              height: 36,
              padding: 0,
              borderRadius: "12px",
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.primary.border2}`,
              color: theme.palette.primary.main,
            },
            "& .MuiIconButton-root .MuiSvgIcon-root": {
              fontSize: 20,
            },
            "& .MuiIconButton-root.MuiIconButton-sizeSmall": {
              width: 36,
              height: 36,
              padding: 0,
            },
            "& .MuiIconButton-root.MuiIconButton-sizeLarge": {
              width: 36,
              height: 36,
              padding: 0,
            },
            "& .MuiCard-root .MuiIconButton-root": {
              width: 30,
              height: 30,
            },
            "& .MuiCard-root .MuiIconButton-root .MuiSvgIcon-root": {
              fontSize: 18,
            },
            "& .MuiIconButton-root:hover": {
              backgroundColor: theme.palette.action.hover,
            },
          }),
      ...sx,
    }}
  >
    {children}
  </Box>
);

const SETTINGS_NAV_GROUPS = [
  {
    title: "Account",
    items: [
      { label: "Profile", value: 0, roles: ["Admin", "Member"] },
      { label: "Timesheets", value: 1, roles: ["Admin", "Member"] },
    ],
  },
  {
    title: "Operations",
    items: [
      { label: "Languages", value: 2, roles: ["Admin", "Member"] },
      { label: "Notifications", value: 12, roles: ["Admin", "Member"] },
      { label: "Service Documents", value: 13, roles: ["Admin", "Member"] },
      { label: "Service ServiceTypes", value: 15, roles: ["Admin"] },
      { label: "Service Cost Types", value: 14, roles: ["Admin", "Member"] },
      { label: "Expenses", value: 5, roles: ["Admin"] },
      { label: "Units", value: 6, roles: ["Admin", "Member"] },
      { label: "Holidays", value: 7, roles: ["Admin", "Member"] },
    ],
  },
  {
    title: "Interpretations",
    items: [
      {
        label: "Communication Templates",
        value: 3,
        roles: ["Admin", "Member"],
      },
      { label: "Mileage Rates", value: 4, roles: ["Admin", "Member"] },
    ],
  },
  {
    title: "Linguists",
    items: [
      { label: "Requirements", value: 8, roles: ["Admin", "Member"] },
      { label: "Linguist Documents", value: 9, roles: ["Admin", "Member"] },
    ],
  },
  //{
  //  title: "Linguist Portal",
  //  items: [{ label: "Email Templates", value: 16, roles: ["Admin"] }],
  //},
  {
    title: "Clients",
    items: [
      { label: "Payment Methods", value: 10, roles: ["Admin"] },
      { label: "Client Service Types", value: 11, roles: ["Admin"] },
    ],
  },
];

const DocumentTemplateSortDialogContent = ({
  theme,
  initialTemplates,
  onDraftChange,
}) => {
  const [draft, setDraft] = useState(() => [...initialTemplates]);

  useEffect(() => {
    onDraftChange(draft);
  }, [draft, onDraftChange]);

  const handleDragEnd = (result) => {
    if (!result?.destination) return;

    const reordered = [...draft];
    const [movedItem] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, movedItem);
    setDraft(reordered);
  };

  return (
    <Box
      sx={{
        width: { xs: "calc(100vw - 96px)", sm: 540 },
        maxWidth: "100%",
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Drag templates to reorder. Save will persist the new order.
      </Typography>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable
          droppableId="document-template-dialog-sort-list"
          type="DOCUMENT_TEMPLATE"
          direction="vertical"
          ignoreContainerClipping={false}
          isDropDisabled={false}
          isCombineEnabled={false}
        >
          {(provided, snapshot) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                maxHeight: "55vh",
                overflowY: "auto",
                overflowX: "hidden",
                backgroundColor: snapshot.isDraggingOver
                  ? theme.palette.action.hover
                  : "transparent",
                borderRadius: "10px",
                p: 0.5,
                boxSizing: "border-box",
                transition: "background-color 0.2s ease",
              }}
            >
              {draft.map((template, index) => (
                <Draggable
                  key={template.id}
                  draggableId={`document-template-dialog-${template.id}`}
                  index={index}
                  isDragDisabled={false}
                  disableInteractiveElementBlocking
                  shouldRespectForcePress={false}
                >
                  {(dragProvided, dragSnapshot) => (
                    <Box
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      {...dragProvided.dragHandleProps}
                      style={dragProvided.draggableProps.style}
                      sx={{
                        p: 1.25,
                        width: "100%",
                        boxSizing: "border-box",
                        bgcolor: dragSnapshot.isDragging
                          ? theme.palette.action.selected
                          : theme.palette.background.alt,
                        borderRadius: "10px",
                        border: `1px solid ${theme.palette.primary.main}`,
                        boxShadow: dragSnapshot.isDragging
                          ? theme.shadows[4]
                          : "none",
                      }}
                    >
                      <Typography variant="body1" fontWeight={600} noWrap>
                        {template.Name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block" }}
                      >
                        File: {template.OriginalFileName}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block" }}
                      >
                        Position: {index + 1}
                      </Typography>
                    </Box>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>
    </Box>
  );
};

const Settings = () => {
  usePageTitle("Settings");

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { currentUser, setCurrentUser } = useAuth();
  const {
    setOverlayData,
    setOverlayName,
    showSnackbar,
    openDialog,
    closeDialog,
  } = useStateContext();
  const { socket } = useWebSocket();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const [displayName, setDisplayName] = useState(currentUser?.Name || "");
  const [languages, setLanguages] = useState([]);
  const [loadingLanguages, setLoadingLanguages] = useState(false);
  const [searchLanguage, setSearchLanguage] = useState("");
  const [communicationTemplates, setCommunicationTemplates] = useState([]);
  const [loadingCommunicationTemplates, setLoadingCommunicationTemplates] =
    useState(false);
  const [mileageRates, setMileageRates] = useState([]);
  const [loadingMileageRates, setLoadingMileageRates] = useState(false);
  const [units, setUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [loadingHolidays, setLoadingHolidays] = useState(false);
  const [searchHoliday, setSearchHoliday] = useState("");
  const [linguistRequirements, setLinguistRequirements] = useState([]);
  const [loadingLinguistRequirements, setLoadingLinguistRequirements] =
    useState(false);
  const [searchLinguistRequirement, setSearchLinguistRequirement] =
    useState("");
  const [documentTemplates, setDocumentTemplates] = useState([]);
  const [loadingDocumentTemplates, setLoadingDocumentTemplates] =
    useState(false);
  const [searchDocumentTemplates, setSearchDocumentTemplates] = useState("");
  const [serviceDocuments, setServiceDocuments] = useState([]);
  const [loadingServiceDocuments, setLoadingServiceDocuments] = useState(false);
  const [searchServiceDocuments, setSearchServiceDocuments] = useState("");
  const documentTemplateSortDraftRef = useRef([]);
  const clientServiceTypesFetchInFlightRef = useRef(false);
  const serviceSubtypesFetchInFlightRef = useRef(false);
  const timesheetsTabRef = useRef(null);
  const [timesheetsDateRangeProps, setTimesheetsDateRangeProps] =
    useState(null);
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [isTimesheetStatusLoading, setIsTimesheetStatusLoading] =
    useState(false);
  const [isClockActionLoading, setIsClockActionLoading] = useState(false);
  const [clientPaymentMethods, setClientPaymentMethods] = useState([]);
  const [loadingClientPaymentMethods, setLoadingClientPaymentMethods] =
    useState(false);
  const [clientServiceTypesSettings, setClientServiceTypesSettings] = useState(
    [],
  );
  const [
    loadingClientServiceTypesSettings,
    setLoadingClientServiceTypesSettings,
  ] = useState(false);
  const [serviceSubtypesSettings, setServiceSubtypesSettings] = useState([]);
  const [loadingServiceSubtypesSettings, setLoadingServiceSubtypesSettings] =
    useState(false);
  const [serviceCostTypesSettings, setServiceCostTypesSettings] = useState([]);
  const [loadingServiceCostTypesSettings, setLoadingServiceCostTypesSettings] =
    useState(false);
  const [searchServiceCostTypes, setSearchServiceCostTypes] = useState("");
  const [notificationSettingsData, setNotificationSettingsData] = useState({
    defaults: [],
    memberRules: [],
    memberPreference: null,
    ruleOptions: {},
    soundOptions: [],
    deadlineTime: "09:00:00",
  });
  const [loadingNotificationSettings, setLoadingNotificationSettings] =
    useState(false);
  const [profileImage, setProfileImage] = useState(currentUser?.Image || null);
  const [profileImageThumbnail, setProfileImageThumbnail] = useState(
    currentUser?.ImageThumbnail || null,
  );
  const [tabValue, setTabValue] = useState(0);
  const isInternalQuerySyncRef = useRef(false);
  const userRole = currentUser?.Role || "Member";
  const isTimesheetsHiddenForCurrentUser = useMemo(() => {
    return Number(currentUser?.id) === 1;
  }, [currentUser?.id]);

  const fetchTimesheetStatus = useCallback(
    async ({ silent = true } = {}) => {
      if (!currentUser?.id) {
        setIsClockedIn(false);
        return;
      }

      if (!silent) {
        setIsTimesheetStatusLoading(true);
      }

      try {
        const response = await api.get("/timesheets/me/status");
        setIsClockedIn(Boolean(response?.data?.isClockedIn));
      } catch (error) {
        // Keep current UI state on transient failure.
      } finally {
        if (!silent) {
          setIsTimesheetStatusLoading(false);
        }
      }
    },
    [currentUser?.id],
  );

  const handleTimesheetClockToggle = useCallback(async () => {
    if (isClockActionLoading) return;

    setIsClockActionLoading(true);
    try {
      const requestedHourlyRate = Number(
        currentUser?.HourlyRate ?? currentUser?.hourlyRate ?? 0,
      );
      const payload =
        Number.isFinite(requestedHourlyRate) && requestedHourlyRate > 0
          ? { hourlyRateSnapshot: requestedHourlyRate }
          : {};

      if (isClockedIn) {
        await api.post("/timesheets/clock-out", payload);
        showSnackbar("Clocked out successfully", theme, "success");
      } else {
        await api.post("/timesheets/clock-in", payload);
        showSnackbar("Clocked in successfully", theme, "success");
      }

      await fetchTimesheetStatus({ silent: true });
      await timesheetsTabRef.current?.refreshTimesheets?.();
    } catch (error) {
      showSnackbar(
        error?.response?.data?.error || "Failed to update timesheet status",
        theme,
        "error",
      );
    } finally {
      setIsClockActionLoading(false);
    }
  }, [
    currentUser?.HourlyRate,
    currentUser?.hourlyRate,
    fetchTimesheetStatus,
    isClockActionLoading,
    isClockedIn,
    showSnackbar,
    theme,
  ]);

  const openManualTimeslotOverlay = useCallback(() => {
    setOverlayData(undefined);
    setOverlayName(OVERLAY_NAMES.SETTINGS_TIMESHEETS_MANUAL_NEW);
  }, [setOverlayData, setOverlayName]);

  const handleAddTimeslotClick = useCallback(() => {
    openManualTimeslotOverlay();
  }, [openManualTimeslotOverlay]);

  const visibleSettingsNavGroups = useMemo(() => {
    return SETTINGS_NAV_GROUPS.map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        if (item.value === 1 && isTimesheetsHiddenForCurrentUser) return false;
        if (!item.roles || item.roles.length === 0) return true;
        return item.roles.includes(userRole);
      }),
    })).filter((group) => group.items.length > 0);
  }, [isTimesheetsHiddenForCurrentUser, userRole]);

  const visibleTabValues = useMemo(() => {
    return visibleSettingsNavGroups.flatMap((group) =>
      group.items.map((item) => item.value),
    );
  }, [visibleSettingsNavGroups]);

  useEffect(() => {
    if (visibleTabValues.length === 0) return;
    const params = new URLSearchParams(location.search);
    const raw = params.get("tab");
    const tabFromQuery = Number(raw);
    const hasValidQueryTab =
      Number.isInteger(tabFromQuery) && visibleTabValues.includes(tabFromQuery);

    // Ignore the query update we just wrote ourselves.
    if (isInternalQuerySyncRef.current) {
      isInternalQuerySyncRef.current = false;
      return;
    }

    // Browser back/forward or deep link should restore selected tab.
    if (hasValidQueryTab) {
      setTabValue((prev) => (prev === tabFromQuery ? prev : tabFromQuery));
      return;
    }

    // If current tab is no longer visible (role/nav change), fallback once.
    setTabValue((prev) =>
      visibleTabValues.includes(prev) ? prev : visibleTabValues[0],
    );
  }, [location.search, visibleTabValues]);

  useEffect(() => {
    if (visibleTabValues.length === 0) return;
    if (!visibleTabValues.includes(tabValue)) return;

    const currentQueryTab = searchParams.get("tab");
    const targetTab = String(tabValue);
    if (currentQueryTab === targetTab) return;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", targetTab);
    isInternalQuerySyncRef.current = true;
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams, tabValue, visibleTabValues]);

  const fetchLanguages = useCallback(async () => {
    setLoadingLanguages(true);
    try {
      const response = await api.get("/languages");
      setLanguages((response.data || []).filter((lang) => lang.Name !== "ALL"));
    } catch (err) {
      console.error("Error fetching languages:", err);
      const status = err?.response?.status;
      const userNotFound =
        err?.response?.data?.error === "User not found in database";
      if (status === 401 || status === 403 || status === 404 || userNotFound) {
        return;
      }
      showSnackbar("Error loading languages", theme, "error");
    } finally {
      setLoadingLanguages(false);
    }
  }, [showSnackbar, theme]);

  const fetchCommunicationTemplates = useCallback(async () => {
    setLoadingCommunicationTemplates(true);
    try {
      const response = await api.get("/communicationTemplates");
      setCommunicationTemplates(response.data || []);
    } catch (err) {
      console.error("Error fetching communication templates:", err);
      showSnackbar("Error loading communication templates", theme, "error");
    } finally {
      setLoadingCommunicationTemplates(false);
    }
  }, [showSnackbar, theme]);

  const fetchMileageRates = useCallback(async () => {
    setLoadingMileageRates(true);
    try {
      const response = await api.get("/mileageRates");
      setMileageRates(response.data || []);
    } catch (err) {
      console.error("Error fetching mileage rates:", err);
      showSnackbar("Error loading mileage rates", theme, "error");
    } finally {
      setLoadingMileageRates(false);
    }
  }, [showSnackbar, theme]);

  const fetchUnits = useCallback(async () => {
    setLoadingUnits(true);
    try {
      const response = await api.get("/unitTypes");
      setUnits(response.data || []);
    } catch (err) {
      console.error("Error fetching units:", err);
      showSnackbar("Error loading units", theme, "error");
    } finally {
      setLoadingUnits(false);
    }
  }, [showSnackbar, theme]);

  const fetchHolidays = useCallback(async () => {
    setLoadingHolidays(true);
    try {
      const response = await api.get("/holidays");
      setHolidays(response.data || []);
    } catch (err) {
      console.error("Error fetching holidays:", err);
      showSnackbar("Error loading holidays", theme, "error");
    } finally {
      setLoadingHolidays(false);
    }
  }, [showSnackbar, theme]);

  const fetchLinguistRequirements = useCallback(async () => {
    setLoadingLinguistRequirements(true);
    try {
      const response = await api.get("/linguists/requirements");
      setLinguistRequirements(response.data || []);
    } catch (err) {
      console.error("Error fetching linguist requirements:", err);
      showSnackbar("Error loading linguist requirements", theme, "error");
    } finally {
      setLoadingLinguistRequirements(false);
    }
  }, [showSnackbar, theme]);

  const fetchDocumentTemplates = useCallback(async () => {
    setLoadingDocumentTemplates(true);
    try {
      const response = await api.get("/documentTemplates");
      const templates = Array.isArray(response.data) ? response.data : [];
      const ordered = [...templates].sort((left, right) => {
        const leftSort = Number(left?.SortOrder ?? 0);
        const rightSort = Number(right?.SortOrder ?? 0);
        if (leftSort !== rightSort) return leftSort - rightSort;
        return Number(left?.id || 0) - Number(right?.id || 0);
      });
      setDocumentTemplates(ordered);
    } catch (err) {
      console.error("Error fetching document templates:", err);
      showSnackbar("Error loading document templates", theme, "error");
    } finally {
      setLoadingDocumentTemplates(false);
    }
  }, [showSnackbar, theme]);

  const fetchServiceDocuments = useCallback(async () => {
    setLoadingServiceDocuments(true);
    try {
      const response = await api.get("/serviceDocuments");
      setServiceDocuments(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Error fetching service documents:", err);
      showSnackbar("Error loading service documents", theme, "error");
    } finally {
      setLoadingServiceDocuments(false);
    }
  }, [showSnackbar, theme]);

  const fetchClientPaymentMethods = useCallback(async () => {
    setLoadingClientPaymentMethods(true);
    try {
      const response = await api.get("/clients/settings/paymentMethods");
      setClientPaymentMethods(response.data || []);
    } catch (err) {
      console.error("Error fetching client payment methods settings:", err);
      showSnackbar("Error loading payment methods", theme, "error");
    } finally {
      setLoadingClientPaymentMethods(false);
    }
  }, [showSnackbar, theme]);

  const fetchClientServiceTypesSettings = useCallback(async () => {
    if (clientServiceTypesFetchInFlightRef.current) return;
    clientServiceTypesFetchInFlightRef.current = true;
    setLoadingClientServiceTypesSettings(true);
    try {
      const response = await api.get("/clients/settings/serviceTypes");
      setClientServiceTypesSettings(response.data || []);
    } catch (err) {
      console.error("Error fetching client service types settings:", err);
      showSnackbar("Error loading client service types", theme, "error");
    } finally {
      setLoadingClientServiceTypesSettings(false);
      clientServiceTypesFetchInFlightRef.current = false;
    }
  }, [showSnackbar, theme]);

  const fetchServiceSubtypesSettings = useCallback(async () => {
    if (serviceSubtypesFetchInFlightRef.current) return;
    serviceSubtypesFetchInFlightRef.current = true;
    setLoadingServiceSubtypesSettings(true);
    try {
      const response = await api.get("/services/settings/subtypes");
      setServiceSubtypesSettings(response.data || []);
    } catch (err) {
      console.error("Error fetching service subtypes settings:", err);
      showSnackbar("Error loading service service types", theme, "error");
    } finally {
      setLoadingServiceSubtypesSettings(false);
      serviceSubtypesFetchInFlightRef.current = false;
    }
  }, [showSnackbar, theme]);

  const fetchNotificationSettings = useCallback(async () => {
    setLoadingNotificationSettings(true);
    try {
      const requests = [
        api.get("/notificationSettings/defaults"),
        api.get("/notificationSettings/member/rules"),
        api.get("/notificationSettings/member/preferences"),
        api.get("/notificationSettings/deadline-time"),
      ];

      const [
        defaultsResponse,
        rulesResponse,
        preferencesResponse,
        deadlineTimeResponse,
      ] = await Promise.all(requests);

      setNotificationSettingsData({
        defaults: defaultsResponse.data || [],
        memberRules: rulesResponse.data || [],
        memberPreference: preferencesResponse.data?.preference || null,
        ruleOptions: preferencesResponse.data?.ruleOptions || {},
        soundOptions: preferencesResponse.data?.soundOptions || [],
        deadlineTime: deadlineTimeResponse.data?.time || "09:00:00",
      });
    } catch (err) {
      console.error("Error fetching notification settings:", err);
      showSnackbar("Error loading notification settings", theme, "error");
    } finally {
      setLoadingNotificationSettings(false);
    }
  }, [showSnackbar, theme]);

  const fetchServiceCostTypesSettings = useCallback(async () => {
    setLoadingServiceCostTypesSettings(true);
    try {
      const response = await api.get("/serviceCosts/types/settings");
      const rows = Array.isArray(response.data) ? response.data : [];
      setServiceCostTypesSettings(
        rows.filter(
          (item) => String(item?.Name || "").toLowerCase() !== "vendor cost",
        ),
      );
    } catch (err) {
      console.error("Error fetching service cost types settings:", err);
      showSnackbar("Error loading service cost types", theme, "error");
    } finally {
      setLoadingServiceCostTypesSettings(false);
    }
  }, [showSnackbar, theme]);

  // Fetch languages on mount
  useEffect(() => {
    fetchLanguages();
  }, [fetchLanguages]);

  useEffect(() => {
    if (tabValue === 3) {
      fetchCommunicationTemplates();
    }
    if (tabValue === 4) {
      fetchMileageRates();
    }
    if (tabValue === 6) {
      fetchUnits();
    }
    if (tabValue === 7) {
      fetchHolidays();
    }
    if (tabValue === 8) {
      fetchLinguistRequirements();
    }
    if (tabValue === 9) {
      fetchDocumentTemplates();
    }
    if (tabValue === 10) {
      fetchClientPaymentMethods();
    }
    if (tabValue === 11) {
      fetchClientServiceTypesSettings();
    }
    if (tabValue === 15) {
      fetchServiceSubtypesSettings();
    }
    if (tabValue === 12) {
      fetchNotificationSettings();
    }
    if (tabValue === 13) {
      fetchServiceDocuments();
    }
    if (tabValue === 14) {
      fetchServiceCostTypesSettings();
    }
  }, [
    tabValue,
    fetchCommunicationTemplates,
    fetchDocumentTemplates,
    fetchHolidays,
    fetchLinguistRequirements,
    fetchMileageRates,
    fetchUnits,
    fetchClientPaymentMethods,
    fetchClientServiceTypesSettings,
    fetchServiceSubtypesSettings,
    fetchNotificationSettings,
    fetchServiceDocuments,
    fetchServiceCostTypesSettings,
  ]);

  useEffect(() => {
    setDisplayName(currentUser?.Name || "");
    // Preserve existing images if undefined in refreshed payload
    setProfileImage((prev) =>
      currentUser?.Image !== undefined ? (currentUser.Image ?? prev) : prev,
    );
    setProfileImageThumbnail((prev) =>
      currentUser?.ImageThumbnail !== undefined
        ? (currentUser.ImageThumbnail ?? prev)
        : prev,
    );
  }, [currentUser?.Name, currentUser?.Image, currentUser?.ImageThumbnail]);

  useEffect(() => {
    if (tabValue === 1 && timesheetsTabRef.current) {
      const dateRangeProps = timesheetsTabRef.current?.getDateRangeSelector?.();
      if (dateRangeProps) {
        setTimesheetsDateRangeProps(dateRangeProps);
      }

      fetchTimesheetStatus({ silent: true });
    }
  }, [tabValue, fetchTimesheetStatus]);

  useEffect(() => {
    if (!socket || !currentUser?.id) return;

    const handleTimesheetUpdated = ({ userId }) => {
      if (Number(userId) !== Number(currentUser.id)) return;

      fetchTimesheetStatus({ silent: true });
      if (tabValue === 1) {
        void timesheetsTabRef.current?.refreshTimesheets?.();
      }
    };

    socket.on("timesheet:updated", handleTimesheetUpdated);

    return () => {
      socket.off("timesheet:updated", handleTimesheetUpdated);
    };
  }, [socket, currentUser?.id, fetchTimesheetStatus, tabValue]);

  // Listen for settings updates
  useEffect(() => {
    if (!socket) return;

    const handleSettingsUpdate = (data) => {
      const updatedByUserId = String(data?.updatedBy?.userId || "");
      const currentUserId = String(currentUser?.id || "");

      // Local saves already refresh via onSaved callback; skip echoed same-user WS updates.
      if (
        updatedByUserId &&
        currentUserId &&
        updatedByUserId === currentUserId
      ) {
        return;
      }

      // Refetch the appropriate data based on what changed
      if (data.settingsType === "Language") {
        fetchLanguages();
        const userName = data.updatedBy?.userName || "Unknown User";
        const message = `Language ${data.changeAction} by ${userName}`;
        showSnackbar(message, theme, "info");
      } else if (data.settingsType === "MileageRate") {
        fetchMileageRates();
        const userName = data.updatedBy?.userName || "Unknown User";
        const message = `Mileage Rate ${data.changeAction} by ${userName}`;
        showSnackbar(message, theme, "info");
      } else if (data.settingsType === "CommunicationTemplate") {
        fetchCommunicationTemplates();
        const userName = data.updatedBy?.userName || "Unknown User";
        const message = `Communication Template ${data.changeAction} by ${userName}`;
        showSnackbar(message, theme, "info");
      } else if (data.settingsType === "UnitType") {
        fetchUnits();
        const userName = data.updatedBy?.userName || "Unknown User";
        const message = `Unit ${data.changeAction} by ${userName}`;
        showSnackbar(message, theme, "info");
      } else if (data.settingsType === "Holiday") {
        fetchHolidays();
        const userName = data.updatedBy?.userName || "Unknown User";
        const message = `Holiday ${data.changeAction} by ${userName}`;
        showSnackbar(message, theme, "info");
      } else if (data.settingsType === "LinguistRequirementDefinition") {
        fetchLinguistRequirements();
        const userName = data.updatedBy?.userName || "Unknown User";
        const message = `Linguist Requirement ${data.changeAction} by ${userName}`;
        showSnackbar(message, theme, "info");
      } else if (data.settingsType === "DocumentTemplate") {
        fetchDocumentTemplates();
        const userName = data.updatedBy?.userName || "Unknown User";
        const message = `Document Template ${data.changeAction} by ${userName}`;
        showSnackbar(message, theme, "info");
      } else if (data.settingsType === "ServiceDocument") {
        fetchServiceDocuments();
        const userName = data.updatedBy?.userName || "Unknown User";
        const message = `Service Document ${data.changeAction} by ${userName}`;
        showSnackbar(message, theme, "info");
      } else if (data.settingsType === "PaymentMethod") {
        fetchClientPaymentMethods();
        const userName = data.updatedBy?.userName || "Unknown User";
        const message = `Payment Method ${data.changeAction} by ${userName}`;
        showSnackbar(message, theme, "info");
      } else if (data.settingsType === "ServiceType") {
        fetchClientServiceTypesSettings();
        const userName = data.updatedBy?.userName || "Unknown User";
        const message = `Client Service Type ${data.changeAction} by ${userName}`;
        showSnackbar(message, theme, "info");
      } else if (data.settingsType === "ServiceSubtype") {
        fetchServiceSubtypesSettings();
        const userName = data.updatedBy?.userName || "Unknown User";
        const message = `Service ServiceType ${data.changeAction} by ${userName}`;
        showSnackbar(message, theme, "info");
      } else if (data.settingsType === "ServiceCostType") {
        fetchServiceCostTypesSettings();
        const userName = data.updatedBy?.userName || "Unknown User";
        const message = `Service Cost Type ${data.changeAction} by ${userName}`;
        showSnackbar(message, theme, "info");
      } else if (
        [
          "NotificationSetting",
          "NotificationRule",
          "NotificationPreference",
        ].includes(data.settingsType)
      ) {
        fetchNotificationSettings();
        const userName = data.updatedBy?.userName || "Unknown User";
        const message = `Notification ${data.changeAction} by ${userName}`;
        showSnackbar(message, theme, "info");
      }
    };

    socket.on("settings:updated", handleSettingsUpdate);
    return () => socket.off("settings:updated", handleSettingsUpdate);
  }, [
    socket,
    currentUser?.id,
    theme,
    showSnackbar,
    fetchLanguages,
    fetchMileageRates,
    fetchCommunicationTemplates,
    fetchUnits,
    fetchHolidays,
    fetchLinguistRequirements,
    fetchDocumentTemplates,
    fetchServiceDocuments,
    fetchClientPaymentMethods,
    fetchClientServiceTypesSettings,
    fetchServiceSubtypesSettings,
    fetchServiceCostTypesSettings,
    fetchNotificationSettings,
  ]);

  const openNotificationRuleEditor = (rule = null) => {
    setOverlayData({
      rule,
      ruleOptions: notificationSettingsData.ruleOptions,
      onSaved: fetchNotificationSettings,
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_NOTIFICATION_NEW_EDIT);
  };

  const openSystemDefaultRuleEditor = (rule = null) => {
    setOverlayData({
      rule: rule
        ? { ...rule, IsSystemDefault: true }
        : { IsSystemDefault: true },
      ruleOptions: notificationSettingsData.ruleOptions,
      onSaved: fetchNotificationSettings,
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_NOTIFICATION_NEW_EDIT);
  };

  // Listen for member updates (for current user's profile)
  useEffect(() => {
    if (!socket || !currentUser?.id) return;

    const handleMemberUpdate = (data) => {
      // Only update if this is the current user's profile
      if (data.recordId === currentUser.id) {
        // Refetch the current user data
        api
          .get(`/members/id/${currentUser.id}`)
          .then((response) => {
            if (setCurrentUser && response.data) {
              setCurrentUser((prev) => ({
                ...prev,
                Name: response.data?.Name ?? prev?.Name,
                Image: response.data?.Image ?? prev?.Image,
                ImageThumbnail:
                  response.data?.ImageThumbnail ?? prev?.ImageThumbnail,
                HourlyRate: response.data?.HourlyRate ?? prev?.HourlyRate,
                hourlyRate: response.data?.HourlyRate ?? prev?.hourlyRate,
              }));

              const userName = data.updatedBy?.userName || "Unknown User";
              const message =
                userName === currentUser.Name
                  ? `Profile updated successfully`
                  : `Your profile was ${data.changeAction} by ${userName}`;
              showSnackbar(message, theme, "info");
            }
          })
          .catch((err) => {
            console.error("Error refetching user data:", err);
          });
      }
    };

    socket.on("member:updated", handleMemberUpdate);
    return () => socket.off("member:updated", handleMemberUpdate);
  }, [
    socket,
    currentUser?.id,
    currentUser?.Name,
    theme,
    showSnackbar,
    setCurrentUser,
  ]);

  const filteredLanguages = useMemo(() => {
    return languages.filter((lang) =>
      lang.Name.toLowerCase().includes(searchLanguage.toLowerCase()),
    );
  }, [languages, searchLanguage]);

  const filteredHolidays = useMemo(() => {
    return holidays.filter((holiday) =>
      holiday.Name.toLowerCase().includes(searchHoliday.toLowerCase()),
    );
  }, [holidays, searchHoliday]);

  const holidaysByYear = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison

    const grouped = {};
    filteredHolidays.forEach((holiday) => {
      const holidayDate = new Date(holiday.Date);

      // Only include holidays that are today or in the future
      if (holidayDate >= today) {
        const year = holidayDate.getFullYear();
        if (!grouped[year]) {
          grouped[year] = [];
        }
        grouped[year].push(holiday);
      }
    });

    // Sort each year's holidays by date
    Object.keys(grouped).forEach((year) => {
      grouped[year].sort((a, b) => new Date(a.Date) - new Date(b.Date));
    });

    return grouped;
  }, [filteredHolidays]);

  const openProfileEditor = () => {
    setOverlayData({
      memberId: currentUser?.id,
      name: displayName,
      email: currentUser?.email || "",
      hourlyRate: currentUser?.HourlyRate ?? currentUser?.hourlyRate ?? 0,
      image: profileImage,
      imageThumbnail: profileImageThumbnail,
      onProfileUpdated: ({
        name,
        email,
        hourlyRate,
        image,
        imageThumbnail,
      }) => {
        if (name) setDisplayName(name);
        if (image !== undefined) setProfileImage(image);
        if (imageThumbnail !== undefined)
          setProfileImageThumbnail(imageThumbnail);
        // Update global auth user so Navbar and other consumers refresh
        if (setCurrentUser) {
          setCurrentUser((prev) => ({
            ...prev,
            Name: name ?? prev?.Name,
            email: email ?? prev?.email,
            HourlyRate: hourlyRate ?? prev?.HourlyRate,
            hourlyRate: hourlyRate ?? prev?.hourlyRate,
            Image: image ?? prev?.Image,
            ImageThumbnail: imageThumbnail ?? prev?.ImageThumbnail,
          }));
        }
      },
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_PROFILE_EDIT);
  };

  const openLanguageEditor = (languageId, languageName) => {
    setOverlayData({
      languageId,
      languageName,
      allLanguages: languages,
      onLanguagesSaved: fetchLanguages,
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_LANGUAGE_NEW_EDIT);
  };

  const openLanguageCreator = () => {
    setOverlayData({
      languageId: null,
      languageName: "",
      allLanguages: languages,
      onLanguagesSaved: fetchLanguages,
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_LANGUAGE_NEW_EDIT);
  };

  const openCommunicationTemplateCreator = () => {
    setOverlayData({
      template: null,
      onSaved: fetchCommunicationTemplates,
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_EMAIL_TEMPLATE_NEW_EDIT);
  };

  const openCommunicationTemplateEditor = (template) => {
    setOverlayData({
      template,
      onSaved: fetchCommunicationTemplates,
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_EMAIL_TEMPLATE_NEW_EDIT);
  };

  const openMileageRateCreator = () => {
    setOverlayData({
      mileageRateId: null,
      year: "",
      rate: "",
      allMileageRates: mileageRates,
      onMileageRatesSaved: fetchMileageRates,
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_MILEAGE_RATE_NEW_EDIT);
  };

  const openMileageRateEditor = (mileageRate) => {
    setOverlayData({
      mileageRateId: mileageRate.id,
      year: mileageRate.year,
      rate: mileageRate.rate,
      allMileageRates: mileageRates,
      onMileageRatesSaved: fetchMileageRates,
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_MILEAGE_RATE_NEW_EDIT);
  };

  const openUnitCreator = () => {
    setOverlayData({
      unitId: null,
      name: "",
      abbreviation: "",
      active: true,
      allUnits: units,
      onUnitsSaved: fetchUnits,
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_UNIT_NEW_EDIT);
  };

  const openUnitEditor = (unit) => {
    setOverlayData({
      unitId: unit.id,
      name: unit.Name,
      abbreviation: unit.Abbreviation,
      active: unit.Active,
      allUnits: units,
      onUnitsSaved: fetchUnits,
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_UNIT_NEW_EDIT);
  };

  const openHolidayEditor = (holiday) => {
    setOverlayData({
      holidayId: holiday.id,
      holidayName: holiday.Name,
      holidayDate: holiday.Date,
      holidayType: holiday.Type,
      holidayDescription: holiday.Description,
      holidayIsActive: holiday.IsActive,
      allHolidays: holidays,
      onHolidaysSaved: fetchHolidays,
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_HOLIDAY_NEW_EDIT);
  };

  const openHolidayCreator = () => {
    setOverlayData({
      holidayId: null,
      holidayName: "",
      holidayDate: null,
      holidayType: "Federal",
      holidayDescription: "",
      holidayIsActive: true,
      allHolidays: holidays,
      onHolidaysSaved: fetchHolidays,
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_HOLIDAY_NEW_EDIT);
  };

  const openRequirementCreator = () => {
    setOverlayData({
      requirementId: null,
      requirement: null,
      onSaved: fetchLinguistRequirements,
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_LINGUIST_REQUIREMENT_NEW_EDIT);
  };

  const openRequirementEditor = (requirement) => {
    setOverlayData({
      requirementId: requirement.id,
      requirement,
      onSaved: fetchLinguistRequirements,
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_LINGUIST_REQUIREMENT_NEW_EDIT);
  };

  const filteredLinguistRequirements = useMemo(() => {
    const query = searchLinguistRequirement.trim().toLowerCase();
    if (!query) return linguistRequirements;

    return linguistRequirements.filter((requirement) => {
      const haystack = `${requirement.Name || ""} ${
        requirement.Key || ""
      } ${requirement.SourceColumn || ""} ${requirement.Description || ""}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [linguistRequirements, searchLinguistRequirement]);

  const filteredDocumentTemplates = useMemo(() => {
    const query = searchDocumentTemplates.trim().toLowerCase();
    if (!query) return documentTemplates;

    return documentTemplates.filter((template) => {
      const haystack =
        `${template.Name || ""} ${template.OriginalFileName || ""} ${template.MimeType || ""}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [documentTemplates, searchDocumentTemplates]);

  const filteredServiceDocuments = useMemo(() => {
    const query = searchServiceDocuments.trim().toLowerCase();
    if (!query) return serviceDocuments;

    return serviceDocuments.filter((document) => {
      const haystack =
        `${document.Name || ""} ${document.Description || ""} ${document.OriginalFileName || ""}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [serviceDocuments, searchServiceDocuments]);

  const filteredServiceCostTypes = useMemo(() => {
    const query = searchServiceCostTypes.trim().toLowerCase();
    if (!query) return serviceCostTypesSettings;

    return serviceCostTypesSettings.filter((item) => {
      const haystack =
        `${item.Name || ""} ${item.Description || ""}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [serviceCostTypesSettings, searchServiceCostTypes]);

  const serviceSubtypesByCategory = useMemo(() => {
    const grouped = {
      Interpretation: [],
      Translation: [],
      Other: [],
    };

    for (const subtype of serviceSubtypesSettings) {
      const category = String(subtype?.ServiceCategory || "");
      if (grouped[category]) {
        grouped[category].push(subtype);
      }
    }

    const compareServiceSubtypes = (left, right) => {
      const leftActive = Boolean(left?.IsActive);
      const rightActive = Boolean(right?.IsActive);
      if (leftActive !== rightActive) {
        return leftActive ? -1 : 1;
      }

      const leftSort = Number(left?.SortOrder ?? 0);
      const rightSort = Number(right?.SortOrder ?? 0);
      if (leftSort !== rightSort) {
        return leftSort - rightSort;
      }

      return String(left?.Name || "").localeCompare(String(right?.Name || ""));
    };

    Object.keys(grouped).forEach((category) => {
      grouped[category].sort(compareServiceSubtypes);
    });

    return grouped;
  }, [serviceSubtypesSettings]);

  const saveDocumentTemplateSortOrder = async (templateDraft) => {
    try {
      const payload = templateDraft.map((template, index) => ({
        id: template.id,
        sortOrder: index + 1,
      }));

      await api.put("/documentTemplates/sort-order/bulk", {
        templateOrders: payload,
      });

      const updated = templateDraft.map((template, index) => ({
        ...template,
        SortOrder: index + 1,
      }));
      setDocumentTemplates(updated);
      showSnackbar("Template order updated", theme, "success");
    } catch (err) {
      console.error("Error saving template order:", err);
      showSnackbar(
        err?.response?.data?.error || "Failed to save template order",
        theme,
        "error",
      );
    }
  };

  const openDocumentTemplateSortDialog = () => {
    documentTemplateSortDraftRef.current = [...documentTemplates];

    openDialog({
      theme,
      title: "Sort Linguist Documents",
      customContent: (
        <DocumentTemplateSortDialogContent
          theme={theme}
          initialTemplates={documentTemplates}
          onDraftChange={(nextDraft) => {
            documentTemplateSortDraftRef.current = nextDraft;
          }}
        />
      ),
      primaryAction: "Save",
      secondaryAction: "Cancel",
      primaryOnClick: async () => {
        await saveDocumentTemplateSortOrder(
          documentTemplateSortDraftRef.current || [],
        );
        closeDialog();
      },
      secondaryOnClick: closeDialog,
    });
  };

  const openDocumentTemplateCreator = () => {
    setOverlayData({
      templateId: null,
      template: null,
      mode: "new",
      onSaved: fetchDocumentTemplates,
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_DOCUMENT_TEMPLATE_NEW_EDIT);
  };

  const openDocumentTemplateEditor = (template) => {
    setOverlayData({
      templateId: template.id,
      template,
      mode: "edit",
      onSaved: fetchDocumentTemplates,
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_DOCUMENT_TEMPLATE_NEW_EDIT);
  };

  const withTemplateBlob = async (templateId, mode = "view") => {
    const response = await api.get(
      `/documentTemplates/${templateId}/file?mode=${mode}`,
      {
        responseType: "blob",
      },
    );
    return response.data;
  };

  const handleViewTemplate = async (template) => {
    try {
      const blob = await withTemplateBlob(template.id, "view");
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      console.error("Error viewing template:", err);
      showSnackbar("Unable to open template", theme, "error");
    }
  };

  const handleDownloadTemplate = async (template) => {
    try {
      const blob = await withTemplateBlob(template.id, "download");
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download =
        template.OriginalFileName || `${template.Name || "template"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Error downloading template:", err);
      showSnackbar("Unable to download template", theme, "error");
    }
  };

  const handleDeleteTemplate = (template) => {
    openDialog({
      theme,
      title: "Delete Document Template",
      message: `Are you sure you want to delete "${template.Name}"?`,
      primaryAction: "Delete",
      secondaryAction: "Cancel",
      primaryOnClick: async () => {
        try {
          await api.delete(`/documentTemplates/${template.id}`);
          closeDialog();
          showSnackbar("Template deleted", theme, "success");
          fetchDocumentTemplates();
        } catch (err) {
          console.error("Error deleting template:", err);
          closeDialog();
          showSnackbar(
            err?.response?.data?.error || "Error deleting template",
            theme,
            "error",
          );
        }
      },
      secondaryOnClick: closeDialog,
    });
  };

  const openServiceDocumentCreator = () => {
    setOverlayData({
      documentId: null,
      document: null,
      mode: "new",
      onSaved: fetchServiceDocuments,
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_SERVICE_DOCUMENT_NEW_EDIT);
  };

  const openServiceDocumentEditor = (document) => {
    setOverlayData({
      documentId: document.id,
      document,
      mode: "edit",
      onSaved: fetchServiceDocuments,
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_SERVICE_DOCUMENT_NEW_EDIT);
  };

  const handleDownloadServiceDocumentTemplate = async (serviceDocument) => {
    try {
      const response = await api.get(
        `/serviceDocuments/${serviceDocument.id}/file`,
        {
          responseType: "blob",
        },
      );

      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download =
        serviceDocument.OriginalFileName ||
        `${serviceDocument.Name || "template"}.docx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Error downloading service document template:", err);
      showSnackbar("Unable to download template", theme, "error");
    }
  };

  const handleDeleteServiceDocument = (document) => {
    openDialog({
      theme,
      title: "Delete Service Document",
      message: `Are you sure you want to delete "${document.Name}"?`,
      primaryAction: "Delete",
      secondaryAction: "Cancel",
      primaryOnClick: async () => {
        try {
          await api.delete(`/serviceDocuments/${document.id}`);
          closeDialog();
          showSnackbar("Service document deleted", theme, "success");
          fetchServiceDocuments();
        } catch (err) {
          console.error("Error deleting service document:", err);
          closeDialog();
          showSnackbar(
            err?.response?.data?.error || "Error deleting service document",
            theme,
            "error",
          );
        }
      },
      secondaryOnClick: closeDialog,
    });
  };

  const openPaymentMethodCreator = () => {
    setOverlayData({
      paymentMethodId: null,
      name: "",
      usageCount: 0,
      allPaymentMethods: clientPaymentMethods,
      onSaved: fetchClientPaymentMethods,
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_PAYMENT_METHOD_NEW_EDIT);
  };

  const openPaymentMethodEditor = (paymentMethod) => {
    setOverlayData({
      paymentMethodId: paymentMethod.id,
      name: paymentMethod.Name,
      usageCount: paymentMethod.usageCount || 0,
      allPaymentMethods: clientPaymentMethods,
      onSaved: fetchClientPaymentMethods,
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_PAYMENT_METHOD_NEW_EDIT);
  };

  const openServiceTypeCreator = () => {
    setOverlayData({
      entityType: "clientServiceType",
      serviceTypeId: null,
      name: "",
      usageCount: 0,
      allServiceTypes: clientServiceTypesSettings,
      onSaved: fetchClientServiceTypesSettings,
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_SERVICE_TYPE_NEW_EDIT);
  };

  const openServiceTypeEditor = (serviceType) => {
    setOverlayData({
      entityType: "clientServiceType",
      serviceTypeId: serviceType.id,
      name: serviceType.Name,
      usageCount: serviceType.usageCount || 0,
      allServiceTypes: clientServiceTypesSettings,
      onSaved: fetchClientServiceTypesSettings,
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_SERVICE_TYPE_NEW_EDIT);
  };

  const openServiceSubtypeCreator = () => {
    setOverlayData({
      entityType: "serviceSubtype",
      serviceSubtypeId: null,
      name: "",
      serviceCategory: "Interpretation",
      isActive: true,
      sortOrder: 0,
      usageCount: 0,
      allServiceTypes: serviceSubtypesSettings,
      onSaved: fetchServiceSubtypesSettings,
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_SERVICE_TYPE_NEW_EDIT);
  };

  const openServiceSubtypeEditor = (serviceSubtype) => {
    setOverlayData({
      entityType: "serviceSubtype",
      serviceSubtypeId: serviceSubtype.id,
      name: serviceSubtype.Name,
      serviceCategory: serviceSubtype.ServiceCategory,
      isActive: Boolean(serviceSubtype.IsActive),
      sortOrder: Number(serviceSubtype.SortOrder || 0),
      usageCount: serviceSubtype.usageCount || 0,
      allServiceTypes: serviceSubtypesSettings,
      onSaved: fetchServiceSubtypesSettings,
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_SERVICE_TYPE_NEW_EDIT);
  };

  const openServiceCostTypeCreator = () => {
    setOverlayData({
      costTypeId: null,
      name: "",
      description: "",
      isActive: true,
      defaultIncludeInTotalCost: true,
      defaultIncludeInAccountCharge: false,
      sortOrder: 0,
      usageCount: 0,
      allServiceCostTypes: serviceCostTypesSettings,
      onSaved: fetchServiceCostTypesSettings,
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_SERVICE_COST_TYPE_NEW_EDIT);
  };

  const openServiceCostTypeEditor = (costType) => {
    setOverlayData({
      costTypeId: costType.id,
      name: costType.Name,
      description: costType.Description || "",
      isActive: Boolean(costType.IsActive),
      defaultIncludeInTotalCost: Boolean(costType.DefaultIncludeInTotalCost),
      defaultIncludeInAccountCharge: Boolean(
        costType.DefaultIncludeInAccountCharge,
      ),
      visibleToLinguists: Boolean(costType.VisibleToLinguists),
      sortOrder: Number(costType.SortOrder || 0),
      usageCount: Number(costType.usageCount || 0),
      allServiceCostTypes: serviceCostTypesSettings,
      onSaved: fetchServiceCostTypesSettings,
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_SERVICE_COST_TYPE_NEW_EDIT);
  };

  const openServiceCostTypeRulesEditor = (costType) => {
    setOverlayData({
      costTypeId: costType.id,
      costTypeName: costType.Name,
      defaultIncludeInTotalCost: Boolean(costType.DefaultIncludeInTotalCost),
      defaultIncludeInAccountCharge: Boolean(
        costType.DefaultIncludeInAccountCharge,
      ),
      onSaved: fetchServiceCostTypesSettings,
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_SERVICE_COST_TYPE_RULES_EDIT);
  };

  return (
    <Box
      p={isMobile ? "1rem 1rem 0 1rem" : "1rem 1rem 0 0"}
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        height: `calc(100vh - ${NAVBAR_HEIGHT + 20}px)`,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          mt: "1rem",
          mx: isMobile ? "0.5rem" : "0rem",
          pb: 3,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          flex: 1,
          minHeight: 0,
        }}
      >
        <GroupedSidebarNav
          groups={visibleSettingsNavGroups}
          value={tabValue}
          onChange={setTabValue}
          sx={{
            minWidth: { xs: "100%", sm: 240 },
            maxHeight: { xs: "none", sm: "100%" },
            flexShrink: 0,
          }}
        />

        {/* Content Area */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Profile Tab */}
          {tabValue === 0 && (
            <SettingsCard
              theme={theme}
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  minHeight: 0,
                }}
              >
                <Box sx={{ flexShrink: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant={isMobile ? "h4" : "h3"}
                      fontWeight="bold"
                    >
                      Profile
                    </Typography>
                    <IconButton
                      aria-label="Edit profile"
                      onClick={openProfileEditor}
                      sx={(theme) => ({
                        height: 36,
                        width: 36,
                        p: 0,
                        borderRadius: "12px",
                        backgroundColor: theme.palette.background.default,
                        border: "1px solid",
                        borderColor: theme.palette.divider,
                        color: theme.palette.primary.main,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        "&:hover": {
                          borderColor: theme.palette.primary.main,
                        },
                        "&:focus-visible": {
                          borderColor: theme.palette.primary.main,
                          outline: "none",
                        },
                      })}
                    >
                      <EditRoundedIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                </Box>

                <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Box sx={{ minWidth: 160 }}>
                      <Typography variant="caption" color="text.secondary">
                        Profile Picture
                      </Typography>
                    </Box>
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        bgcolor: getAvatarColor(currentUser?.Name),
                        borderRadius: "12px",
                        boxShadow: theme.shadows[3],
                      }}
                      src={profileImageThumbnail || profileImage || undefined}
                    >
                      {currentUser?.Name?.charAt(0).toUpperCase()}
                    </Avatar>
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Box sx={{ minWidth: 160 }}>
                      <Typography variant="caption" color="text.secondary">
                        Email
                      </Typography>
                    </Box>
                    <Typography variant="body1">
                      {currentUser?.email || ""}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Box sx={{ minWidth: 160 }}>
                      <Typography variant="caption" color="text.secondary">
                        Display Name
                      </Typography>
                    </Box>
                    <Typography variant="body1">{displayName}</Typography>
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ minWidth: 160 }}>
                      <Typography variant="caption" color="text.secondary">
                        Password
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body1" sx={{ mr: 2 }}>
                        ********
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Use Edit to change your password
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box>
                    <Typography
                      variant={isMobile ? "h4" : "h3"}
                      fontWeight="bold"
                      gutterBottom
                    >
                      Activity & Statistics
                    </Typography>
                    {currentUser?.id ? (
                      <MemberActivity memberId={currentUser.id} />
                    ) : null}
                  </Box>
                </Box>
              </Box>
            </SettingsCard>
          )}

          {/* Preferences Tab */}
          {tabValue === 1 && (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <SettingsCard
                theme={theme}
                disableIconButtonOverrides
                sx={{
                  flexShrink: 0,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    minHeight: 0,
                  }}
                >
                  {/* Header Section */}
                  <Box sx={{ flexShrink: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        //mb: 2,
                      }}
                    >
                      <Typography
                        variant={isMobile ? "h4" : "h3"}
                        fontWeight="bold"
                      >
                        Timesheets
                      </Typography>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        {timesheetsDateRangeProps && (
                          <CustomDateSelector
                            {...timesheetsDateRangeProps}
                            useSimpleStyle
                          />
                        )}
                        <IconButton
                          aria-label={isClockedIn ? "Clock out" : "Clock in"}
                          onClick={handleTimesheetClockToggle}
                          disabled={
                            isClockActionLoading || isTimesheetStatusLoading
                          }
                          sx={{
                            height: 36,
                            width: 36,
                            p: 0,
                            borderRadius: "12px",
                            border: "1px solid",
                            borderColor: isClockedIn
                              ? theme.palette.error.main
                              : theme.palette.primary.main,
                            color: isClockedIn
                              ? theme.palette.error.main
                              : theme.palette.primary.main,
                            backgroundColor: theme.palette.background.default,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            "&:hover": {
                              borderColor: isClockedIn
                                ? theme.palette.error.main
                                : theme.palette.primary.main,
                              backgroundColor: theme.palette.action.hover,
                            },
                            "&:focus-visible": {
                              borderColor: isClockedIn
                                ? theme.palette.error.main
                                : theme.palette.primary.main,
                              outline: "none",
                            },
                          }}
                        >
                          {isClockActionLoading || isTimesheetStatusLoading ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : isClockedIn ? (
                            <StopRoundedIcon sx={{ fontSize: 18 }} />
                          ) : (
                            <PlayArrowRoundedIcon sx={{ fontSize: 18 }} />
                          )}
                        </IconButton>
                        <IconButton
                          aria-label="Add manual timeslot"
                          onClick={handleAddTimeslotClick}
                          sx={{
                            height: 36,
                            width: 36,
                            p: 0,
                            borderRadius: "12px",
                            backgroundColor: theme.palette.background.default,
                            border: "1px solid",
                            borderColor: theme.palette.divider,
                            color: theme.palette.primary.main,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            "&:hover": {
                              borderColor: theme.palette.primary.main,
                            },
                            "&:focus-visible": {
                              borderColor: theme.palette.primary.main,
                              outline: "none",
                            },
                          }}
                        >
                          <AddRoundedIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>

                  {/* Content Section - will be replaced by SettingsGridCard below */}
                </Box>
              </SettingsCard>

              <SettingsGridCard
                theme={theme}
                disableIconButtonOverrides
                sx={{
                  mt: 1.5,
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <TimesheetsTab
                  ref={timesheetsTabRef}
                  isMobile={isMobile}
                  hideHeader
                  useOuterGridCard
                  hourlyRate={currentUser?.HourlyRate}
                  onDateRangePropsChange={setTimesheetsDateRangeProps}
                />
              </SettingsGridCard>
            </Box>
          )}

          {/* Languages Tab */}
          {tabValue === 2 && (
            <SettingsCard
              theme={theme}
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  minHeight: 0,
                }}
              >
                {/* Fixed Header Section */}
                <Box sx={{ flexShrink: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant={isMobile ? "h4" : "h3"}
                      fontWeight="bold"
                    >
                      Languages
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <TextField
                        size="small"
                        placeholder="Search languages..."
                        value={searchLanguage}
                        onChange={(e) => setSearchLanguage(e.target.value)}
                        variant="outlined"
                        sx={{
                          minWidth: 260,
                          "& .MuiOutlinedInput-root": {
                            height: 36,
                            borderRadius: "12px",
                            backgroundColor: theme.palette.background.default,
                            border: "1px solid",
                            borderColor: theme.palette.divider,
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.palette.divider,
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.palette.primary.main,
                          },
                          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                            {
                              borderColor: theme.palette.primary.main,
                              borderWidth: 2,
                            },
                        }}
                      />
                      <IconButton
                        aria-label="Add language"
                        onClick={openLanguageCreator}
                        sx={(theme) => ({
                          height: 36,
                          width: 36,
                          p: 0,
                          borderRadius: "12px",
                          backgroundColor: theme.palette.background.default,
                          border: "1px solid",
                          borderColor: theme.palette.divider,
                          color: theme.palette.primary.main,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          "&:hover": {
                            borderColor: theme.palette.primary.main,
                          },
                          "&:focus-visible": {
                            borderColor: theme.palette.primary.main,
                            outline: "none",
                          },
                        })}
                      >
                        <AddRoundedIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />
                </Box>

                {/* Scrollable Content Section */}
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                  }}
                >
                  {loadingLanguages ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", p: 3 }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : filteredLanguages.length === 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        p: 3,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {searchLanguage
                          ? "No languages match your search"
                          : "No languages added yet"}
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, minmax(0, 1fr))",
                          lg: "repeat(3, minmax(0, 1fr))",
                        },
                        gap: 1.5,
                      }}
                    >
                      {filteredLanguages.map((language) => (
                        <Box
                          key={language.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 1.5,
                            bgcolor: theme.palette.background.alt,
                            borderRadius: "8px",
                            border: `1px solid ${theme.palette.primary.border2}`,
                            transition: "all 0.2s ease",
                            "&:hover": {
                              bgcolor: theme.palette.action.hover,
                              borderColor: theme.palette.primary.main,
                            },
                            group: "language-item",
                          }}
                          className="language-item"
                        >
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" fontWeight="500" noWrap>
                              {language.Name}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() =>
                              openLanguageEditor(language.id, language.Name)
                            }
                            sx={{
                              opacity: 0,
                              transition: "opacity 0.2s ease",
                              ml: 1,
                              flexShrink: 0,
                              ".language-item:hover &": {
                                opacity: 1,
                              },
                            }}
                            className="edit-button"
                          >
                            <EditRoundedIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            </SettingsCard>
          )}

          {/* Email Templates Tab */}
          {tabValue === 3 && (
            <SettingsCard
              theme={theme}
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  minHeight: 0,
                  minWidth: 0,
                }}
              >
                <Box sx={{ flexShrink: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      mb: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography
                      variant={isMobile ? "h4" : "h3"}
                      fontWeight="bold"
                      sx={{ flexShrink: 0 }}
                    >
                      Communication Templates
                    </Typography>
                    <IconButton
                      aria-label="Add communication template"
                      onClick={openCommunicationTemplateCreator}
                      sx={(theme) => ({
                        height: 36,
                        width: 36,
                        p: 0,
                        borderRadius: "12px",
                        backgroundColor: theme.palette.background.default,
                        border: "1px solid",
                        borderColor: theme.palette.divider,
                        color: theme.palette.primary.main,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        "&:hover": {
                          borderColor: theme.palette.primary.main,
                        },
                        "&:focus-visible": {
                          borderColor: theme.palette.primary.main,
                          outline: "none",
                        },
                      })}
                    >
                      <AddRoundedIcon />
                    </IconButton>
                  </Box>

                  <Divider sx={{ my: 2 }} />
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                >
                  {loadingCommunicationTemplates ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", p: 3 }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : communicationTemplates.length === 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "90%",
                        p: 3,
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textAlign: "center" }}
                      >
                        No communication templates yet. Create one to get
                        started.
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        minWidth: 0,
                      }}
                    >
                      {/* Templates Section */}
                      {communicationTemplates.filter(
                        (t) => t.Type === "Template" || !t.Type,
                      ).length > 0 && (
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              sm: "repeat(2, minmax(0, 1fr))",
                              lg: "repeat(3, minmax(0, 1fr))",
                            },
                            gap: 1.5,
                            minWidth: 0,
                          }}
                        >
                          {communicationTemplates
                            .filter((t) => t.Type === "Template" || !t.Type)
                            .map((template) => (
                              <Box
                                key={template.id}
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  justifyContent: "space-between",
                                  gap: 1.5,
                                  p: 1.5,
                                  bgcolor: theme.palette.background.alt,
                                  borderRadius: "10px",
                                  border: `1px solid ${theme.palette.primary.border2}`,
                                  transition: "all 0.2s ease",
                                  maxWidth: "100%",
                                  "&:hover": {
                                    bgcolor: theme.palette.action.hover,
                                    borderColor: theme.palette.primary.main,
                                  },
                                }}
                              >
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Typography
                                    variant="body1"
                                    fontWeight={600}
                                    noWrap
                                  >
                                    {template.Name}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mt: 0.5 }}
                                    noWrap
                                  >
                                    {template.Description ||
                                      "No description provided"}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    noWrap
                                  >
                                    Subject: {template.Subject}
                                  </Typography>
                                </Box>

                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    flexShrink: 0,
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    color={
                                      template.IsActive
                                        ? "success.main"
                                        : "text.secondary"
                                    }
                                  >
                                    {template.IsActive ? "Active" : "Inactive"}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      openCommunicationTemplateEditor(template)
                                    }
                                    sx={{ ml: 0.5 }}
                                    aria-label={`Edit ${template.Name}`}
                                  >
                                    <EditRoundedIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                            ))}
                        </Box>
                      )}

                      {/* Modifiers Section */}
                      {communicationTemplates.filter(
                        (t) => t.Type === "Modifier",
                      ).length > 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            minWidth: 0,
                          }}
                        >
                          <Typography
                            variant="h5"
                            fontWeight="bold"
                            sx={{ mt: 1 }}
                          >
                            Modifiers
                          </Typography>
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, minmax(0, 1fr))",
                                lg: "repeat(3, minmax(0, 1fr))",
                              },
                              gap: 1.5,
                              minWidth: 0,
                            }}
                          >
                            {communicationTemplates
                              .filter((t) => t.Type === "Modifier")
                              .map((template) => (
                                <Box
                                  key={template.id}
                                  sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    justifyContent: "space-between",
                                    gap: 1.5,
                                    p: 1.5,
                                    bgcolor: theme.palette.background.alt,
                                    borderRadius: "10px",
                                    border: `1px solid ${theme.palette.primary.border2}`,
                                    transition: "all 0.2s ease",
                                    maxWidth: "100%",
                                    "&:hover": {
                                      bgcolor: theme.palette.action.hover,
                                      borderColor: theme.palette.primary.main,
                                    },
                                  }}
                                >
                                  <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography
                                      variant="body1"
                                      fontWeight={600}
                                      noWrap
                                    >
                                      {template.Name}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ mt: 0.5 }}
                                      noWrap
                                    >
                                      {template.Description ||
                                        "No description provided"}
                                    </Typography>
                                  </Box>

                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      flexShrink: 0,
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      color={
                                        template.IsActive
                                          ? "success.main"
                                          : "text.secondary"
                                      }
                                    >
                                      {template.IsActive
                                        ? "Active"
                                        : "Inactive"}
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        openCommunicationTemplateEditor(
                                          template,
                                        )
                                      }
                                      sx={{ ml: 0.5 }}
                                      aria-label={`Edit ${template.Name}`}
                                    >
                                      <EditRoundedIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Box>
                              ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            </SettingsCard>
          )}

          {/* Mileage Rates Tab */}
          {tabValue === 4 && (
            <SettingsCard
              theme={theme}
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  minHeight: 0,
                }}
              >
                {/* Fixed Header Section */}
                <Box sx={{ flexShrink: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant={isMobile ? "h4" : "h3"}
                      fontWeight="bold"
                    >
                      Mileage Rates
                    </Typography>
                    <IconButton
                      aria-label="Add mileage rate"
                      onClick={openMileageRateCreator}
                      sx={(theme) => ({
                        height: 36,
                        width: 36,
                        p: 0,
                        borderRadius: "12px",
                        backgroundColor: theme.palette.background.default,
                        border: "1px solid",
                        borderColor: theme.palette.divider,
                        color: theme.palette.primary.main,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        "&:hover": {
                          borderColor: theme.palette.primary.main,
                        },
                        "&:focus-visible": {
                          borderColor: theme.palette.primary.main,
                          outline: "none",
                        },
                      })}
                    >
                      <AddRoundedIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Box>

                  <Divider sx={{ my: 2 }} />
                </Box>

                {/* Scrollable Content Section */}
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                  }}
                >
                  {loadingMileageRates ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", p: 3 }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : mileageRates.length === 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "90%",
                        p: 3,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No mileage rates configured yet.
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, minmax(0, 1fr))",
                          lg: "repeat(3, minmax(0, 1fr))",
                        },
                        gap: 1.5,
                      }}
                    >
                      {mileageRates.map((rate) => (
                        <Box
                          key={rate.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 1.5,
                            bgcolor: theme.palette.background.alt,
                            borderRadius: "10px",
                            border: `1px solid ${theme.palette.primary.border2}`,
                            transition: "all 0.2s ease",
                            minHeight: 84,
                            "&:hover": {
                              bgcolor: theme.palette.action.hover,
                              borderColor: theme.palette.primary.main,
                            },
                          }}
                        >
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body1" fontWeight={600}>
                              Year {rate.year}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 0.5 }}
                            >
                              Rate: ${parseFloat(rate.rate).toFixed(3)} per mile
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => openMileageRateEditor(rate)}
                            sx={{ ml: 1 }}
                            aria-label={`Edit year ${rate.year}`}
                          >
                            <EditRoundedIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            </SettingsCard>
          )}

          {/* Expenses Tab */}
          {tabValue === 5 && (
            <SettingsCard
              theme={theme}
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "auto",
              }}
            >
              <ExpensesSettings />
            </SettingsCard>
          )}

          {/* Units Tab */}
          {tabValue === 6 && (
            <SettingsCard
              theme={theme}
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  minHeight: 0,
                }}
              >
                {/* Fixed Header Section */}
                <Box sx={{ flexShrink: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant={isMobile ? "h4" : "h3"}
                      fontWeight="bold"
                    >
                      Units
                    </Typography>
                    <IconButton
                      aria-label="Add unit"
                      onClick={openUnitCreator}
                      sx={(theme) => ({
                        height: 36,
                        width: 36,
                        p: 0,
                        borderRadius: "12px",
                        backgroundColor: theme.palette.background.default,
                        border: "1px solid",
                        borderColor: theme.palette.divider,
                        color: theme.palette.primary.main,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        "&:hover": {
                          borderColor: theme.palette.primary.main,
                        },
                        "&:focus-visible": {
                          borderColor: theme.palette.primary.main,
                          outline: "none",
                        },
                      })}
                    >
                      <AddRoundedIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Box>

                  <Divider sx={{ my: 2 }} />
                </Box>

                {/* Scrollable Content Section */}
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                  }}
                >
                  {loadingUnits ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", p: 3 }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : units.length === 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "90%",
                        p: 3,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No units configured yet.
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, minmax(0, 1fr))",
                          lg: "repeat(3, minmax(0, 1fr))",
                        },
                        gap: 1.5,
                      }}
                    >
                      {units.map((unit) => (
                        <Box
                          key={unit.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 1.5,
                            bgcolor: theme.palette.background.alt,
                            borderRadius: "10px",
                            border: `1px solid ${theme.palette.primary.border2}`,
                            transition: "all 0.2s ease",
                            minHeight: 84,
                            "&:hover": {
                              bgcolor: theme.palette.action.hover,
                              borderColor: theme.palette.primary.main,
                            },
                          }}
                        >
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body1" fontWeight={600}>
                              {unit.Name}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 0.5 }}
                            >
                              Abbreviation: {unit.Abbreviation}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flexShrink: 0,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color={
                                unit.Active ? "success.main" : "text.secondary"
                              }
                            >
                              {unit.Active ? "Active" : "Inactive"}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => openUnitEditor(unit)}
                              sx={{ ml: 0.5 }}
                              aria-label={`Edit ${unit.Name}`}
                            >
                              <EditRoundedIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            </SettingsCard>
          )}

          {/* Holidays Tab */}
          {tabValue === 7 && (
            <SettingsCard
              theme={theme}
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  minHeight: 0,
                }}
              >
                {/* Fixed Header Section */}
                <Box sx={{ flexShrink: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant={isMobile ? "h4" : "h3"}
                      fontWeight="bold"
                    >
                      Holidays
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <TextField
                        size="small"
                        placeholder="Search holidays..."
                        value={searchHoliday}
                        onChange={(e) => setSearchHoliday(e.target.value)}
                        variant="outlined"
                        sx={{
                          minWidth: 260,
                          "& .MuiOutlinedInput-root": {
                            height: 36,
                            borderRadius: "12px",
                            backgroundColor: theme.palette.background.default,
                            border: "1px solid",
                            borderColor: theme.palette.divider,
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.palette.divider,
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.palette.primary.main,
                          },
                          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                            {
                              borderColor: theme.palette.primary.main,
                              borderWidth: 2,
                            },
                        }}
                      />
                      <IconButton
                        aria-label="Add holiday"
                        onClick={openHolidayCreator}
                        sx={(theme) => ({
                          height: 36,
                          width: 36,
                          p: 0,
                          borderRadius: "12px",
                          backgroundColor: theme.palette.background.default,
                          border: "1px solid",
                          borderColor: theme.palette.divider,
                          color: theme.palette.primary.main,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          "&:hover": {
                            borderColor: theme.palette.primary.main,
                          },
                          "&:focus-visible": {
                            borderColor: theme.palette.primary.main,
                            outline: "none",
                          },
                        })}
                      >
                        <AddRoundedIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />
                </Box>

                {/* Scrollable Content Section */}
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                  }}
                >
                  {loadingHolidays ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", p: 3 }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : filteredHolidays.length === 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        p: 3,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {searchHoliday
                          ? "No holidays match your search"
                          : "No holidays added yet"}
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 3,
                      }}
                    >
                      {Object.keys(holidaysByYear)
                        .sort((a, b) => a - b)
                        .map((year) => (
                          <Box key={year}>
                            <Typography
                              variant="h5"
                              fontWeight="bold"
                              sx={{
                                mb: 1.5,
                                color: theme.palette.primary.main,
                              }}
                            >
                              {year}
                            </Typography>
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                  xs: "1fr",
                                  sm: "repeat(2, 1fr)",
                                  lg: "repeat(3, minmax(0, 1fr))",
                                },
                                gap: 1.5,
                              }}
                            >
                              {holidaysByYear[year].map((holiday) => (
                                <Box
                                  key={holiday.id}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    p: 1.5,
                                    bgcolor: theme.palette.background.alt,
                                    borderRadius: "10px",
                                    border: `1px solid ${theme.palette.primary.border2}`,
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                      bgcolor: theme.palette.action.hover,
                                      borderColor: theme.palette.primary.main,
                                    },
                                  }}
                                >
                                  <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography
                                      variant="body1"
                                      fontWeight={600}
                                    >
                                      {holiday.Name}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ mt: 0.5 }}
                                    >
                                      {new Date(
                                        holiday.Date,
                                      ).toLocaleDateString("en-US", {
                                        weekday: "short",
                                        month: "short",
                                        day: "numeric",
                                      })}{" "}
                                      • {holiday.Type}
                                    </Typography>
                                    {holiday.Description && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ mt: 0.5, display: "block" }}
                                      >
                                        {holiday.Description}
                                      </Typography>
                                    )}
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      flexShrink: 0,
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      color={
                                        holiday.IsActive
                                          ? "success.main"
                                          : "text.secondary"
                                      }
                                    >
                                      {holiday.IsActive ? "Active" : "Inactive"}
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() => openHolidayEditor(holiday)}
                                      sx={{ ml: 0.5 }}
                                      aria-label={`Edit ${holiday.Name}`}
                                    >
                                      <EditRoundedIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        ))}
                    </Box>
                  )}
                </Box>
              </Box>
            </SettingsCard>
          )}

          {/* Linguist Requirements Tab */}
          {tabValue === 8 && (
            <SettingsCard
              theme={theme}
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  minHeight: 0,
                }}
              >
                <Box sx={{ flexShrink: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant={isMobile ? "h4" : "h3"}
                      fontWeight="bold"
                    >
                      Linguist Requirements
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <TextField
                        size="small"
                        placeholder="Search requirements..."
                        value={searchLinguistRequirement}
                        onChange={(event) =>
                          setSearchLinguistRequirement(event.target.value)
                        }
                        variant="outlined"
                        sx={{
                          minWidth: 260,
                          "& .MuiOutlinedInput-root": {
                            height: 36,
                            borderRadius: "12px",
                            backgroundColor: theme.palette.background.default,
                            border: "1px solid",
                            borderColor: theme.palette.divider,
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.palette.divider,
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.palette.primary.main,
                          },
                          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                            {
                              borderColor: theme.palette.primary.main,
                              borderWidth: 2,
                            },
                        }}
                      />
                      <IconButton
                        aria-label="Add requirement"
                        onClick={openRequirementCreator}
                        sx={(theme) => ({
                          height: 36,
                          width: 36,
                          p: 0,
                          borderRadius: "12px",
                          backgroundColor: theme.palette.background.default,
                          border: "1px solid",
                          borderColor: theme.palette.divider,
                          color: theme.palette.primary.main,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          "&:hover": {
                            borderColor: theme.palette.primary.main,
                          },
                          "&:focus-visible": {
                            borderColor: theme.palette.primary.main,
                            outline: "none",
                          },
                        })}
                      >
                        <AddRoundedIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />
                </Box>

                <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                  {loadingLinguistRequirements ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", p: 3 }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : filteredLinguistRequirements.length === 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        p: 3,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {searchLinguistRequirement
                          ? "No requirements match your search"
                          : "No requirement definitions yet"}
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, minmax(0, 1fr))",
                          lg: "repeat(3, minmax(0, 1fr))",
                        },
                        gap: 1.5,
                      }}
                    >
                      {filteredLinguistRequirements.map((requirement) => (
                        <Box
                          key={requirement.id}
                          sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            alignItems: { xs: "flex-start", sm: "center" },
                            justifyContent: "space-between",
                            p: 1.5,
                            bgcolor: theme.palette.background.alt,
                            borderRadius: "10px",
                            border: `1px solid ${theme.palette.primary.border2}`,
                            transition: "all 0.2s ease",
                            minHeight: { xs: "unset", sm: 150 },
                            "&:hover": {
                              bgcolor: theme.palette.action.hover,
                              borderColor: theme.palette.primary.main,
                            },
                          }}
                        >
                          <Box sx={{ minWidth: 0, flex: 1, width: "100%" }}>
                            <Typography variant="body1" fontWeight={600}>
                              {requirement.Name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Key: {requirement.Key} | Column:{" "}
                              {requirement.SourceColumn}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block" }}
                            >
                              {requirement.Description || "No description"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color={
                                requirement.Usage?.hasTrueValues
                                  ? "warning.main"
                                  : "text.secondary"
                              }
                              sx={{ display: "block" }}
                            >
                              Usage: {requirement.Usage?.totalValues || 0}{" "}
                              values, {requirement.Usage?.trueValues || 0} true
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              width: { xs: "100%", sm: "auto" },
                              justifyContent: {
                                xs: "space-between",
                                sm: "flex-end",
                              },
                              mt: { xs: 1, sm: 0 },
                              flexShrink: 0,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color={
                                requirement.IsActive
                                  ? "success.main"
                                  : "text.secondary"
                              }
                            >
                              {requirement.IsActive ? "Active" : "Archived"}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => openRequirementEditor(requirement)}
                              aria-label={`Edit ${requirement.Name}`}
                            >
                              <EditRoundedIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            </SettingsCard>
          )}

          {/* Linguist Documents Tab */}
          {tabValue === 9 && (
            <SettingsCard
              theme={theme}
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  minHeight: 0,
                }}
              >
                <Box sx={{ flexShrink: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant={isMobile ? "h4" : "h3"}
                      fontWeight="bold"
                    >
                      Linguists / Linguist Documents
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <TextField
                        size="small"
                        placeholder="Search linguist documents..."
                        value={searchDocumentTemplates}
                        onChange={(event) =>
                          setSearchDocumentTemplates(event.target.value)
                        }
                        variant="outlined"
                        sx={{
                          minWidth: 260,
                          "& .MuiOutlinedInput-root": {
                            height: 36,
                            borderRadius: "12px",
                            backgroundColor: theme.palette.background.default,
                            border: "1px solid",
                            borderColor: theme.palette.divider,
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.palette.divider,
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.palette.primary.main,
                          },
                          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                            {
                              borderColor: theme.palette.primary.main,
                              borderWidth: 2,
                            },
                        }}
                      />
                      <IconButton
                        aria-label="Sort linguist documents"
                        onClick={openDocumentTemplateSortDialog}
                        sx={(theme) => ({
                          height: 36,
                          width: 36,
                          p: 0,
                          borderRadius: "12px",
                          backgroundColor: theme.palette.background.default,
                          border: "1px solid",
                          borderColor: theme.palette.divider,
                          color: theme.palette.primary.main,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          "&:hover": {
                            borderColor: theme.palette.primary.main,
                          },
                          "&:focus-visible": {
                            borderColor: theme.palette.primary.main,
                            outline: "none",
                          },
                        })}
                      >
                        <SortRoundedIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                      <IconButton
                        aria-label="Add linguist document"
                        onClick={openDocumentTemplateCreator}
                        sx={(theme) => ({
                          height: 36,
                          width: 36,
                          p: 0,
                          borderRadius: "12px",
                          backgroundColor: theme.palette.background.default,
                          border: "1px solid",
                          borderColor: theme.palette.divider,
                          color: theme.palette.primary.main,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          "&:hover": {
                            borderColor: theme.palette.primary.main,
                          },
                          "&:focus-visible": {
                            borderColor: theme.palette.primary.main,
                            outline: "none",
                          },
                        })}
                      >
                        <AddRoundedIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />
                </Box>

                <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                  {loadingDocumentTemplates ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", p: 3 }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : filteredDocumentTemplates.length === 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        p: 3,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {searchDocumentTemplates
                          ? "No linguist documents match your search"
                          : "No linguist documents yet"}
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, minmax(0, 1fr))",
                          lg: "repeat(3, minmax(0, 1fr))",
                        },
                        gap: 1.5,
                      }}
                    >
                      {filteredDocumentTemplates.map((template) => (
                        <Box
                          key={template.id}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            p: 1.5,
                            bgcolor: theme.palette.background.alt,
                            borderRadius: "10px",
                            border: `1px solid ${theme.palette.primary.border2}`,
                            transition: "all 0.2s ease",
                            minHeight: 170,
                            "&:hover": {
                              bgcolor: theme.palette.action.hover,
                              borderColor: theme.palette.primary.main,
                            },
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body1" fontWeight={600} noWrap>
                              {template.Name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block" }}
                            >
                              File: {template.OriginalFileName}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block" }}
                            >
                              Size:{" "}
                              {(
                                Number(template.FileSizeBytes || 0) / 1024
                              ).toFixed(1)}{" "}
                              KB | Version: {template.Version}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block" }}
                            >
                              Updated:{" "}
                              {template.UpdatedOn
                                ? new Date(template.UpdatedOn).toLocaleString()
                                : new Date(template.CreatedOn).toLocaleString()}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              mt: 1.25,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color={
                                template.IsActive
                                  ? "success.main"
                                  : "text.secondary"
                              }
                            >
                              {template.IsActive ? "Active" : "Archived"}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                              <Button
                                size="small"
                                onClick={() => handleViewTemplate(template)}
                              >
                                View
                              </Button>
                              <Button
                                size="small"
                                onClick={() => handleDownloadTemplate(template)}
                              >
                                Download
                              </Button>
                              <Button
                                size="small"
                                onClick={() =>
                                  openDocumentTemplateEditor(template)
                                }
                              >
                                Edit
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                onClick={() => handleDeleteTemplate(template)}
                              >
                                Delete
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            </SettingsCard>
          )}

          {/* Client Payment Methods Tab */}
          {tabValue === 10 && (
            <SettingsCard
              theme={theme}
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  minHeight: 0,
                }}
              >
                <Box sx={{ flexShrink: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant={isMobile ? "h4" : "h3"}
                      fontWeight="bold"
                    >
                      Client Payment Methods
                    </Typography>
                    <IconButton
                      aria-label="Add payment method"
                      onClick={openPaymentMethodCreator}
                      sx={(theme) => ({
                        height: 36,
                        width: 36,
                        p: 0,
                        borderRadius: "12px",
                        backgroundColor: theme.palette.background.default,
                        border: "1px solid",
                        borderColor: theme.palette.divider,
                        color: theme.palette.primary.main,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        "&:hover": {
                          borderColor: theme.palette.primary.main,
                        },
                        "&:focus-visible": {
                          borderColor: theme.palette.primary.main,
                          outline: "none",
                        },
                      })}
                    >
                      <AddRoundedIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Box>

                  <Divider sx={{ my: 2 }} />
                </Box>

                <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                  {loadingClientPaymentMethods ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", p: 3 }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : clientPaymentMethods.length === 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        p: 3,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No payment methods configured yet.
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, minmax(0, 1fr))",
                          lg: "repeat(3, minmax(0, 1fr))",
                        },
                        gap: 1.5,
                      }}
                    >
                      {clientPaymentMethods.map((method) => (
                        <Box
                          key={method.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 1.5,
                            bgcolor: theme.palette.background.alt,
                            borderRadius: "10px",
                            border: `1px solid ${theme.palette.primary.border2}`,
                            minHeight: 84,
                          }}
                        >
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body1" fontWeight={600}>
                              {method.Name}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 0.5 }}
                            >
                              Used by {method.usageCount || 0} client
                              {(method.usageCount || 0) === 1 ? "" : "s"}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => openPaymentMethodEditor(method)}
                            sx={{ ml: 0.5 }}
                            aria-label={`Edit ${method.Name}`}
                          >
                            <EditRoundedIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            </SettingsCard>
          )}

          {/* Service Documents Tab */}
          {tabValue === 13 && (
            <SettingsCard
              theme={theme}
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  minHeight: 0,
                }}
              >
                <Box sx={{ flexShrink: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant={isMobile ? "h4" : "h3"}
                      fontWeight="bold"
                    >
                      Service Documents
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <TextField
                        size="small"
                        placeholder="Search service documents..."
                        value={searchServiceDocuments}
                        onChange={(event) =>
                          setSearchServiceDocuments(event.target.value)
                        }
                        variant="outlined"
                        sx={{
                          minWidth: 260,
                          "& .MuiOutlinedInput-root": {
                            height: 36,
                            borderRadius: "12px",
                            backgroundColor: theme.palette.background.default,
                            border: "1px solid",
                            borderColor: theme.palette.divider,
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.palette.divider,
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.palette.primary.main,
                          },
                          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                            {
                              borderColor: theme.palette.primary.main,
                              borderWidth: 2,
                            },
                        }}
                      />
                      <IconButton
                        aria-label="Add service document"
                        onClick={openServiceDocumentCreator}
                        sx={(theme) => ({
                          height: 36,
                          width: 36,
                          p: 0,
                          borderRadius: "12px",
                          backgroundColor: theme.palette.background.default,
                          border: "1px solid",
                          borderColor: theme.palette.divider,
                          color: theme.palette.primary.main,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          "&:hover": {
                            borderColor: theme.palette.primary.main,
                          },
                          "&:focus-visible": {
                            borderColor: theme.palette.primary.main,
                            outline: "none",
                          },
                        })}
                      >
                        <AddRoundedIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />
                </Box>

                <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                  {loadingServiceDocuments ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", p: 3 }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : filteredServiceDocuments.length === 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        p: 3,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {searchServiceDocuments
                          ? "No service documents match your search"
                          : "No service documents yet"}
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, minmax(0, 1fr))",
                          lg: "repeat(3, minmax(0, 1fr))",
                        },
                        gap: 1.5,
                      }}
                    >
                      {filteredServiceDocuments.map((serviceDocument) => (
                        <Box
                          key={serviceDocument.id}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            p: 1.5,
                            bgcolor: theme.palette.background.alt,
                            borderRadius: "10px",
                            border: `1px solid ${theme.palette.primary.border2}`,
                            transition: "all 0.2s ease",
                            minHeight: 180,
                            "&:hover": {
                              bgcolor: theme.palette.action.hover,
                              borderColor: theme.palette.primary.main,
                            },
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body1" fontWeight={600} noWrap>
                              {serviceDocument.Name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block" }}
                            >
                              {serviceDocument.Description || "No description"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block" }}
                            >
                              File: {serviceDocument.OriginalFileName}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block" }}
                            >
                              Fields:{" "}
                              {Array.isArray(serviceDocument.Fields)
                                ? serviceDocument.Fields.length
                                : 0}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              mt: 1.25,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color={
                                serviceDocument.IsActive
                                  ? "success.main"
                                  : "text.secondary"
                              }
                            >
                              {serviceDocument.IsActive ? "Active" : "Archived"}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                              <Button
                                size="small"
                                onClick={() =>
                                  handleDownloadServiceDocumentTemplate(
                                    serviceDocument,
                                  )
                                }
                              >
                                Download
                              </Button>
                              <Button
                                size="small"
                                onClick={() =>
                                  openServiceDocumentEditor(serviceDocument)
                                }
                              >
                                Edit
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                onClick={() =>
                                  handleDeleteServiceDocument(serviceDocument)
                                }
                              >
                                Delete
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            </SettingsCard>
          )}

          {/* Client Service Types Tab */}
          {tabValue === 11 && (
            <SettingsCard
              theme={theme}
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  minHeight: 0,
                }}
              >
                <Box sx={{ flexShrink: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant={isMobile ? "h4" : "h3"}
                      fontWeight="bold"
                    >
                      Client Service Types
                    </Typography>
                    <IconButton
                      aria-label="Add client service type"
                      onClick={openServiceTypeCreator}
                      sx={(theme) => ({
                        height: 36,
                        width: 36,
                        p: 0,
                        borderRadius: "12px",
                        backgroundColor: theme.palette.background.default,
                        border: "1px solid",
                        borderColor: theme.palette.divider,
                        color: theme.palette.primary.main,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        "&:hover": {
                          borderColor: theme.palette.primary.main,
                        },
                        "&:focus-visible": {
                          borderColor: theme.palette.primary.main,
                          outline: "none",
                        },
                      })}
                    >
                      <AddRoundedIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Box>

                  <Divider sx={{ my: 2 }} />
                </Box>

                <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                  {loadingClientServiceTypesSettings ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", p: 3 }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : clientServiceTypesSettings.length === 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        p: 3,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No client service types configured yet.
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, minmax(0, 1fr))",
                          lg: "repeat(3, minmax(0, 1fr))",
                        },
                        gap: 1.5,
                      }}
                    >
                      {clientServiceTypesSettings.map((serviceType) => (
                        <Box
                          key={serviceType.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 1.5,
                            bgcolor: theme.palette.background.alt,
                            borderRadius: "10px",
                            border: `1px solid ${theme.palette.primary.border2}`,
                            minHeight: 84,
                          }}
                        >
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body1" fontWeight={600}>
                              {serviceType.Name}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 0.5 }}
                            >
                              Used by {serviceType.usageCount || 0} client
                              {(serviceType.usageCount || 0) === 1 ? "" : "s"}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => openServiceTypeEditor(serviceType)}
                            sx={{ ml: 0.5 }}
                            aria-label={`Edit ${serviceType.Name}`}
                          >
                            <EditRoundedIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            </SettingsCard>
          )}

          {/* Service ServiceTypes Tab */}
          {tabValue === 15 && (
            <SettingsCard
              theme={theme}
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  minHeight: 0,
                }}
              >
                <Box sx={{ flexShrink: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant={isMobile ? "h4" : "h3"}
                      fontWeight="bold"
                    >
                      Service ServiceTypes
                    </Typography>
                    <IconButton
                      aria-label="Add service servicetype"
                      onClick={openServiceSubtypeCreator}
                      sx={(theme) => ({
                        height: 36,
                        width: 36,
                        p: 0,
                        borderRadius: "12px",
                        backgroundColor: theme.palette.background.default,
                        border: "1px solid",
                        borderColor: theme.palette.divider,
                        color: theme.palette.primary.main,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        "&:hover": {
                          borderColor: theme.palette.primary.main,
                        },
                        "&:focus-visible": {
                          borderColor: theme.palette.primary.main,
                          outline: "none",
                        },
                      })}
                    >
                      <AddRoundedIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Box>

                  <Divider sx={{ my: 2 }} />
                </Box>

                <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                  {loadingServiceSubtypesSettings ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", p: 3 }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : serviceSubtypesSettings.length === 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        p: 3,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No service service types configured yet.
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      {Object.entries(serviceSubtypesByCategory).map(
                        ([category, items]) => {
                          if (!items.length) return null;

                          return (
                            <Box key={category}>
                              <Typography
                                variant="h5"
                                fontWeight="bold"
                                sx={{
                                  mb: 1,
                                  color: theme.palette.primary.main,
                                }}
                              >
                                {category}
                              </Typography>

                              <Box
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: {
                                    xs: "1fr",
                                    sm: "repeat(2, minmax(0, 1fr))",
                                    lg: "repeat(3, minmax(0, 1fr))",
                                  },
                                  gap: 1.5,
                                }}
                              >
                                {items.map((serviceSubtype) => (
                                  <Box
                                    key={serviceSubtype.id}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      p: 1.5,
                                      bgcolor: theme.palette.background.alt,
                                      borderRadius: "10px",
                                      border: `1px solid ${theme.palette.primary.border2}`,
                                      minHeight: 84,
                                    }}
                                  >
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                      <Typography
                                        variant="body1"
                                        fontWeight={600}
                                      >
                                        {serviceSubtype.Name}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mt: 0.25 }}
                                      >
                                        Used by {serviceSubtype.usageCount || 0}{" "}
                                        service
                                        {(serviceSubtype.usageCount || 0) === 1
                                          ? ""
                                          : "s"}
                                        {serviceSubtype.IsActive
                                          ? " | Active"
                                          : " | Archived"}
                                      </Typography>
                                    </Box>
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        openServiceSubtypeEditor(serviceSubtype)
                                      }
                                      sx={{ ml: 0.5 }}
                                      aria-label={`Edit ${serviceSubtype.Name}`}
                                    >
                                      <EditRoundedIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          );
                        },
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            </SettingsCard>
          )}

          {/* Service Cost Types Tab */}
          {tabValue === 14 && (
            <SettingsCard
              theme={theme}
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  minHeight: 0,
                }}
              >
                <Box sx={{ flexShrink: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant={isMobile ? "h4" : "h3"}
                      fontWeight="bold"
                    >
                      Service Cost Types
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <TextField
                        size="small"
                        placeholder="Search cost types..."
                        value={searchServiceCostTypes}
                        onChange={(event) =>
                          setSearchServiceCostTypes(event.target.value)
                        }
                        variant="outlined"
                        sx={{
                          minWidth: 260,
                          "& .MuiOutlinedInput-root": {
                            height: 36,
                            borderRadius: "12px",
                            backgroundColor: theme.palette.background.default,
                            border: "1px solid",
                            borderColor: theme.palette.divider,
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.palette.divider,
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.palette.primary.main,
                          },
                          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                            {
                              borderColor: theme.palette.primary.main,
                              borderWidth: 2,
                            },
                        }}
                      />
                      <IconButton
                        aria-label="Add service cost type"
                        onClick={openServiceCostTypeCreator}
                        sx={(theme) => ({
                          height: 36,
                          width: 36,
                          p: 0,
                          borderRadius: "12px",
                          backgroundColor: theme.palette.background.default,
                          border: "1px solid",
                          borderColor: theme.palette.divider,
                          color: theme.palette.primary.main,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          "&:hover": {
                            borderColor: theme.palette.primary.main,
                          },
                          "&:focus-visible": {
                            borderColor: theme.palette.primary.main,
                            outline: "none",
                          },
                        })}
                      >
                        <AddRoundedIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />
                </Box>

                <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                  {loadingServiceCostTypesSettings ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", p: 3 }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : filteredServiceCostTypes.length === 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        p: 3,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {searchServiceCostTypes
                          ? "No cost types match your search"
                          : "No service cost types configured yet."}
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, minmax(0, 1fr))",
                          lg: "repeat(3, minmax(0, 1fr))",
                        },
                        gap: 1.5,
                      }}
                    >
                      {filteredServiceCostTypes.map((costType) => (
                        <Box
                          key={costType.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 1.5,
                            bgcolor: theme.palette.background.alt,
                            borderRadius: "10px",
                            border: `1px solid ${theme.palette.primary.border2}`,
                            minHeight: 120,
                          }}
                        >
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body1" fontWeight={600}>
                              {costType.Name}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 0.5 }}
                            >
                              {costType.Description || "No description"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mt: 0.5, display: "block" }}
                            >
                              Usage: {costType.usageCount || 0}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mt: 0.25, display: "block" }}
                            >
                              Total Cost:{" "}
                              {costType.DefaultIncludeInTotalCost
                                ? "Yes"
                                : "No"}{" "}
                              | Account Charge:{" "}
                              {costType.DefaultIncludeInAccountCharge
                                ? "Yes"
                                : "No"}{" "}
                              | {costType.IsActive ? "Active" : "Inactive"}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Button
                              size="small"
                              onClick={() =>
                                openServiceCostTypeRulesEditor(costType)
                              }
                            >
                              Rules
                            </Button>
                            <IconButton
                              size="small"
                              onClick={() =>
                                openServiceCostTypeEditor(costType)
                              }
                              sx={{ ml: 0.5 }}
                              aria-label={`Edit ${costType.Name}`}
                            >
                              <EditRoundedIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            </SettingsCard>
          )}

          {tabValue === 12 && (
            <SettingsCard
              theme={theme}
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <NotificationSettingsTab
                theme={theme}
                isMobile={isMobile}
                userRole={userRole}
                settingsData={notificationSettingsData}
                loading={loadingNotificationSettings}
                onRefresh={fetchNotificationSettings}
                onOpenDefaultRule={openSystemDefaultRuleEditor}
                onOpenRule={openNotificationRuleEditor}
              />
            </SettingsCard>
          )}

          {tabValue === 16 && (
            <SettingsCard
              theme={theme}
              sx={{ flex: 1, minHeight: 0, overflow: "auto" }}
            >
              <PortalEmailTemplatesTab theme={theme} />
            </SettingsCard>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Settings;
